import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import "./TransactionsTable.scss";
import {
  useGetAllTransactionsQuery,
  useRefundTransactionMutation,
} from "../../../features/api/transactionApiSlice";
import { generatePageNumbers } from "../../../util/generatePageNumber";
import LoadingSpinner from "../../loader/Loader";
import RefundModal from "../../confirmation/RefundModal";
// Comments at the bottom
const TransactionsTable = () => {
  const [refundTransaction] = useRefundTransactionMutation();
  const [processingId, setProcessingId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [refundTarget, setRefundTarget] = useState(null); // { id, defaultAmount }
  const [refundAmount, setRefundAmount] = useState(""); // controlled modal input

  // ✅ Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 8; // categories per page

  const {
    data: transactions = [], // ✅ safe default so maps never blow up
    isFetching, // use this for spinner
    isError,
    isLoading,
    refetch,
  } = useGetAllTransactionsQuery(undefined, {
    refetchOnMountOrArgChange: "always", // ✅ refetch every time page mounts
    refetchOnFocus: true, // refresh when tab regains focus
    refetchOnReconnect: true, // refresh when network comes back
  });

  useEffect(() => {
    if (!transactions?.length && !isFetching && !isError) {
      refetch(); // one more nudge in case the first request was aborted in dev
    }
  }, [transactions?.length, isFetching, isError, refetch]);

  const [userFilter, setUserFilter] = useState("");
  const [subscriptionFilter, setSubscriptionFilter] = useState("");

  const handleRefund = async (transactionId, amount) => {
    try {
      setProcessingId(transactionId);
      const result = await refundTransaction({
        id: transactionId,
        amount,
      }).unwrap();

      if (result.success) {
        toast.success(result.message || "Refund initiated successfully!");
        refetch();
      } else {
        toast.error(result.message || "Refund failed");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.data?.message || "Refund failed");
    } finally {
      setProcessingId(null);
    }
  };

  // Filtered transactions
  const filteredTransactions = useMemo(() => {
    if (!transactions) return [];
    return transactions.filter((tx) => {
      const matchesUser =
        !userFilter ||
        `${tx.userId?.firstName} ${tx.userId?.lastName}`
          .toLowerCase()
          .includes(userFilter.toLowerCase());
      const matchesSubscription =
        !subscriptionFilter ||
        tx.subscriptionId?.type
          .toLowerCase()
          .includes(subscriptionFilter.toLowerCase());
      return matchesUser && matchesSubscription;
    });
  }, [transactions, userFilter, subscriptionFilter]);

  // Page numbers set
  const totalPages = Math.ceil(filteredTransactions.length / pageSize);

  const currentTransactions = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredTransactions.slice(startIdx, startIdx + pageSize);
  }, [filteredTransactions, currentPage, pageSize]);

  const pageNumbers = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <p>Error loading transactions.</p>;

  return (
    <div className="transactions-container">
      <RefundModal
        isOpen={showConfirm}
        message={`Refund for this transaction?`}
        amount={refundAmount}
        onAmountChange={setRefundAmount}
        onConfirm={() => {
          if (refundTarget && refundAmount > 0) {
            handleRefund(refundTarget.id, parseFloat(refundAmount));
          }
          setShowConfirm(false);
          setRefundTarget(null);
          setRefundAmount("");
        }}
        onCancel={() => {
          setShowConfirm(false);
          setRefundTarget(null);
          setRefundAmount("");
        }}
      />
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Transactions</h1>
        </div>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by user"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
        />
        <input
          type="text"
          placeholder="Filter by subscription"
          value={subscriptionFilter}
          onChange={(e) => setSubscriptionFilter(e.target.value)}
        />
      </div>
      {/* ✅ Admin informational message */}
      <div className="admin-info-message">
        <p>
          Note: Transactions may take time to appear. Subscriptions created
          today may show up by the next business day around 2:00 AM. If a user
          requests a refund immediately after subscribing, it might not yet
          appear in the transaction list.
        </p>
      </div>
      <table className="transactions-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>User</th>
            <th>Subscription</th>
            <th>Amount</th>
            <th>Type</th>
            <th>Charge Status</th>
            <th>Refund Status</th>
            <th>Created At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentTransactions?.map((tx) => (
            <tr key={tx?._id}>
              <td>{tx?._id}</td>
              <td>
                {tx.userId?.firstName} {tx?.userId?.lastName}
              </td>
              <td>{tx?.subscriptionId?.type}</td>
              <td>${tx?.amount.toFixed(2)}</td>
              <td>{tx?.type}</td>
              <td>{tx?.status}</td>
              <td>
                {tx.status === "refund_pending" && (
                  <span className="refund-status pending">
                    Refund Pending: ${tx.refundAmount}
                  </span>
                )}
                {tx.status === "refunded" && (
                  <span className="refund-status completed">
                    Refunded: ${tx.refundAmount} on{" "}
                    {new Date(tx.refundCompletedAt).toLocaleString()}
                  </span>
                )}
                {tx.status === "completed" && !tx.refundAmount && (
                  <span>-</span>
                )}
              </td>
              <td>{new Date(tx?.createdAt).toLocaleString()}</td>
              <td>
                {tx?.status !== "refunded" &&
                tx?.status !== "refund_pending" ? (
                  <button
                    disabled={processingId === tx?._id}
                    onClick={() => {
                      setRefundTarget({
                        id: tx._id,
                        defaultAmount: tx.amount,
                      });
                      setRefundAmount(tx.amount); // set initial value in modal
                      setShowConfirm(true);
                    }}
                    className="refund-btn"
                  >
                    {processingId === tx?._id ? "Processing..." : "Refund"}
                  </button>
                ) : (
                  <span className="no-action">No action</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="pagination responsive-pagination">
          <button
            onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            {"<"}
          </button>
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span className="ellipsis" key={idx}>
                ...
              </span>
            ) : (
              <button
                key={idx}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() =>
              handlePageClick(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default TransactionsTable;

// THIS ONE HAS AN INLINE INPUT IT IS ALSO WORKING JUST HAVENT USED IT
// import { useState, useMemo, useEffect } from "react";
// import toast from "react-hot-toast";
// import "./TransactionsTable.scss";
// import {
//   useGetAllTransactionsQuery,
//   useRefundTransactionMutation,
// } from "../../../features/api/transactionApiSlice";
// import { generatePageNumbers } from "../../../util/generatePageNumber";
// import LoadingSpinner from "../../loader/Loader";
// import ConfirmationModal from "../../confirmation/ConfirmationModal";

// const TransactionsTable = () => {
//   const [refundTransaction] = useRefundTransactionMutation();
//   const [processingId, setProcessingId] = useState(null);
//   const [showConfirm, setShowConfirm] = useState(false);
//   const [refundTarget, setRefundTarget] = useState(null); // { id, amount }
//   const [refundInputs, setRefundInputs] = useState({}); // { transactionId: value }

//   // ✅ Add pagination state
//   const [currentPage, setCurrentPage] = useState(1);
//   const pageSize = 2; // categories per page

//   const {
//     data: transactions = [], // ✅ safe default so maps never blow up
//     isFetching, // use this for spinner
//     isError,
//     isLoading,
//     refetch,
//   } = useGetAllTransactionsQuery(undefined, {
//     refetchOnMountOrArgChange: "always", // ✅ refetch every time page mounts
//     refetchOnFocus: true, // refresh when tab regains focus
//     refetchOnReconnect: true, // refresh when network comes back
//   });

//   useEffect(() => {
//     if (!transactions?.length && !isFetching && !isError) {
//       refetch(); // one more nudge in case the first request was aborted in dev
//     }
//   }, [transactions?.length, isFetching, isError, refetch]);

//   const [userFilter, setUserFilter] = useState("");
//   const [subscriptionFilter, setSubscriptionFilter] = useState("");

//   const handleRefund = async (transactionId, amount) => {
//     try {
//       setProcessingId(transactionId);
//       const result = await refundTransaction({
//         id: transactionId,
//         amount,
//       }).unwrap();

//       if (result.success) {
//         toast.success(result.message || "Refund initiated successfully!");
//         refetch();
//       } else {
//         toast.error(result.message || "Refund failed");
//       }
//     } catch (err) {
//       console.error(err);
//       toast.error(err?.data?.message || "Refund failed");
//     } finally {
//       setProcessingId(null);
//     }
//   };

//   // Filtered transactions
//   const filteredTransactions = useMemo(() => {
//     if (!transactions) return [];
//     return transactions.filter((tx) => {
//       const matchesUser =
//         !userFilter ||
//         `${tx.userId?.firstName} ${tx.userId?.lastName}`
//           .toLowerCase()
//           .includes(userFilter.toLowerCase());
//       const matchesSubscription =
//         !subscriptionFilter ||
//         tx.subscriptionId?.type
//           .toLowerCase()
//           .includes(subscriptionFilter.toLowerCase());
//       return matchesUser && matchesSubscription;
//     });
//   }, [transactions, userFilter, subscriptionFilter]);

//   // Page numbers set
//   const totalPages = Math.ceil(filteredTransactions.length / pageSize);

//   const currentTransactions = useMemo(() => {
//     const startIdx = (currentPage - 1) * pageSize;
//     return filteredTransactions.slice(startIdx, startIdx + pageSize);
//   }, [filteredTransactions, currentPage, pageSize]);

//   const pageNumbers = useMemo(
//     () => generatePageNumbers(currentPage, totalPages),
//     [currentPage, totalPages]
//   );

//   const handlePageClick = (page) => {
//     setCurrentPage(page);
//     window.scrollTo({ top: 0, behavior: "smooth" });
//   };

//   if (isLoading) return <LoadingSpinner />;
//   if (isError) return <p>Error loading transactions.</p>;

//   return (
//     <div className="transactions-container">
//       <ConfirmationModal
//         isOpen={showConfirm}
//         message={`Refund $${refundTarget?.amount} for this transaction?`}
//         onConfirm={() => {
//           if (refundTarget) {
//             handleRefund(refundTarget.id, refundTarget.amount);
//           }
//           setShowConfirm(false);
//           setRefundTarget(null);
//         }}
//         onCancel={() => {
//           setShowConfirm(false);
//           setRefundTarget(null);
//         }}
//       />
//       <div className="dashboard-top-section">
//         <div className="dash-top-flex">
//           <h1 className="dashboard-title">Transactions</h1>
//         </div>
//       </div>
//       <div className="filters">
//         <input
//           type="text"
//           placeholder="Filter by user"
//           value={userFilter}
//           onChange={(e) => setUserFilter(e.target.value)}
//         />
//         <input
//           type="text"
//           placeholder="Filter by subscription"
//           value={subscriptionFilter}
//           onChange={(e) => setSubscriptionFilter(e.target.value)}
//         />
//       </div>

//       <table className="transactions-table">
//         <thead>
//           <tr>
//             <th>ID</th>
//             <th>User</th>
//             <th>Subscription</th>
//             <th>Amount</th>
//             <th>Type</th>
//             <th>Status</th>
//             <th>Created At</th>
//             <th>Actions</th>
//           </tr>
//         </thead>
//         <tbody>
//           {currentTransactions?.map((tx) => (
//             <tr key={tx?._id}>
//               <td>{tx?._id}</td>
//               <td>
//                 {tx.userId?.firstName} {tx?.userId?.lastName}
//               </td>
//               <td>{tx?.subscriptionId?.type}</td>
//               <td>${tx?.amount.toFixed(2)}</td>
//               <td>{tx?.type}</td>
//               <td>{tx?.status}</td>
//               <td>{new Date(tx?.createdAt).toLocaleString()}</td>
//               <td>
//                 {tx?.status !== "refunded" &&
//                 tx?.status !== "refund_pending" ? (
//                   <div className="refund-actions">
//                     <input
//                       type="number"
//                       step="0.01"
//                       min="0.01"
//                       max={tx.maxRefundableAmount || tx.amount} // 👈 prevent over-refund
//                       placeholder={tx.amount.toFixed(2)}
//                       className="refund-input"
//                       value={refundInputs[tx._id] || ""}
//                       onChange={(e) =>
//                         setRefundInputs({
//                           ...refundInputs,
//                           [tx._id]: e.target.value,
//                         })
//                       }
//                     />
//                     <button
//                       disabled={processingId === tx?._id}
//                       onClick={() =>
//                         setRefundTarget({
//                           id: tx._id,
//                           amount: parseFloat(refundInputs[tx._id] || tx.amount),
//                         }) || setShowConfirm(true)
//                       }
//                       className="refund-btn"
//                     >
//                       {processingId === tx?._id ? "Processing..." : "Refund"}
//                     </button>
//                   </div>
//                 ) : (
//                   <span className="no-action">No action</span>
//                 )}
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//       {/* ✅ Pagination */}
//       {totalPages > 1 && (
//         <div className="pagination responsive-pagination">
//           <button
//             onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
//             disabled={currentPage === 1}
//           >
//             {"<"}
//           </button>
//           {pageNumbers.map((page, idx) =>
//             page === "..." ? (
//               <span className="ellipsis" key={idx}>
//                 ...
//               </span>
//             ) : (
//               <button
//                 key={idx}
//                 className={currentPage === page ? "active" : ""}
//                 onClick={() => handlePageClick(page)}
//               >
//                 {page}
//               </button>
//             )
//           )}
//           <button
//             onClick={() =>
//               handlePageClick(Math.min(currentPage + 1, totalPages))
//             }
//             disabled={currentPage === totalPages}
//           >
//             {">"}
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// export default TransactionsTable;
